import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, MapPinned, Navigation, Clock } from "lucide-react";

// Custom car icon
const carIcon = L.divIcon({
  className: "live-car-marker",
  html: `<div style="width:32px;height:32px;border-radius:50%;background:#C05A3A;border:3px solid #fff;box-shadow:0 2px 12px rgba(192,90,58,0.5);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:14px;">🚗</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const DEFAULT_CENTER = [-1.9536, 30.0606];

function AutoFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
    } else {
      map.fitBounds(points, { padding: [30, 30] });
    }
  }, [points, map]);
  return null;
}

function fmtAgo(iso) {
  if (!iso) return "—";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

export default function LiveTripMap({ open, onOpenChange, bookingId, title }) {
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const poll = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/trip`);
      setTrip(data);
      setError(null);
    } catch (e) {
      setError("Couldn't fetch live trip");
    }
  };

  useEffect(() => {
    if (!open || !bookingId) return;
    poll();
    intervalRef.current = setInterval(poll, 15000);
    return () => intervalRef.current && clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingId]);

  const points = trip?.pings?.map((p) => [p.lat, p.lng]) || [];
  const last = points[points.length - 1];
  const lastPing = trip?.pings?.[trip.pings.length - 1];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <MapPinned size={18} className="text-accent" /> Live trip {title ? `— ${title}` : ""}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">Auto-refreshes every 15 seconds. The renter chose to share their location.</DialogDescription>
        </DialogHeader>
        {!trip ? (
          <div className="h-[400px] flex items-center justify-center text-muted-foreground">
            <Loader2 className="animate-spin mr-2" size={16} /> Loading…
          </div>
        ) : trip.pings.length === 0 ? (
          <div className="h-[300px] flex flex-col items-center justify-center text-center px-6">
            <Navigation className="text-muted-foreground mb-2" size={32} />
            <div className="font-display font-semibold">No location pings yet</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm">
              The renter hasn&apos;t turned on live location sharing yet. They can opt in from their booking page.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap text-xs">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="gap-1.5 bg-accent text-accent-foreground"><span className="w-2 h-2 rounded-full bg-white animate-pulse"></span> Live</Badge>
                <Badge variant="outline" className="gap-1"><Clock size={11} /> Last ping {fmtAgo(lastPing?.at)}</Badge>
                <Badge variant="outline">{trip.pings.length} points</Badge>
                {lastPing?.speed != null && lastPing.speed > 0 && (
                  <Badge variant="outline">{(lastPing.speed * 3.6).toFixed(0)} km/h</Badge>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={poll} data-testid="trip-refresh-btn">Refresh</Button>
            </div>
            <div className="rounded-md overflow-hidden border border-border" style={{ height: 420 }} data-testid="live-trip-map">
              <MapContainer center={last || DEFAULT_CENTER} zoom={14} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {points.length > 1 && <Polyline positions={points} pathOptions={{ color: "#C05A3A", weight: 4, opacity: 0.7 }} />}
                {last && <Marker position={last} icon={carIcon} />}
                <AutoFit points={points} />
              </MapContainer>
            </div>
            {error && <div className="text-xs text-destructive mt-2">{error}</div>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
